import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { isAfter, startOfDay } from 'date-fns';
import { InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Textarea } from '@/components/ui/textarea';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES } from '@/lib/constants/pagination';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { DecimalInput } from '@/modules/common/components/molecules/inputs/decimal-form-field';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import {
  createScrollableOptionRenderer,
  createScrollableTriggerRenderer,
} from '@/modules/finance/common/components/scrollable-select-renderers';
import {
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { getShortProjects } from '@/modules/projects';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getOverheadTypeIcon } from '../../constants/overhead-type-icon-map';
import { useCreateOverhead } from '../../hooks/use-create-overhead';
import { useUpdateOverhead } from '../../hooks/use-update-overhead';
import { useUpdateOverheadStatus } from '../../hooks/use-update-overhead-status';
import {
  mapOverheadRecurrenceFrequencyToDTO,
  mapOverheadTypeToDTO,
} from '../../mappers/overhead.mapper';
import {
  OverheadListItem,
  OverheadRecurrenceFrequency,
  OverheadStatus,
  OverheadType,
} from '../../types/overhead';
import { overheadFormSchema, OverheadFormValues } from '../../validations/overhead-form-validation';

const FORM_FIELD_TYPE = 'type';
const FORM_FIELD_PROJECT_ID = 'projectId';
const FORM_FIELD_NAME = 'name';
const FORM_FIELD_DATE = 'date';
const FORM_FIELD_AMOUNT = 'amount';
const FORM_FIELD_RECURRENCE_FREQUENCY = 'recurrenceFrequency';
const FORM_FIELD_RECURRENCE_END_DATE = 'recurrenceEndDate';
const FORM_FIELD_NOTES = 'notes';

const PROJECT_SELECT_QUERY_KEY = ['overheads-project-select'];

const renderProjectOption = createScrollableOptionRenderer({ showColorDot: true });
const renderProjectTrigger = createScrollableTriggerRenderer({ showColorDot: true });

const getDefaultValues = (overhead: OverheadListItem | null): OverheadFormValues => ({
  type: overhead?.type ?? OverheadType.Income,
  projectId: overhead ? String(overhead.project.id) : '',
  name: overhead?.name ?? '',
  date: overhead ? new Date(overhead.date) : new Date(),
  amount: overhead ? String(overhead.amount) : '',
  recurrenceFrequency: OverheadRecurrenceFrequency.OneTime,
  recurrenceEndDate: null,
  notes: overhead?.notes ?? '',
});

interface TypeOptionProps {
  value: OverheadType;
  label: string;
  tooltip: string;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (value: OverheadType) => void;
}

/**
 * One option of the horizontal type selector. A plain controlled element (not
 * a Radix radio group) so switching types never remounts anything — the
 * selection style just flips with the form value. Visuals mirror
 * `CustomRadioGroup` pills (muted pill → filled foreground pill when
 * selected), plus the type icon on the left and an info tooltip on the right.
 */
const TypeOption = ({ value, label, tooltip, isSelected, disabled, onSelect }: TypeOptionProps) => {
  const Icon = getOverheadTypeIcon(value);

  return (
    <div
      role="radio"
      aria-checked={isSelected}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(value)}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(value);
        }
      }}
      className={cn(
        'flex flex-1 items-center justify-between gap-2 rounded-full px-4 py-2 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'bg-foreground text-background'
          : 'cursor-pointer bg-muted text-foreground hover:bg-muted/80',
        disabled && 'cursor-default opacity-50'
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Icon size={16} className="shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <SimpleTooltip content={<p className="max-w-64">{tooltip}</p>}>
        <InfoIcon
          size={14}
          className={cn('shrink-0', isSelected ? 'text-background/70' : 'text-muted-foreground')}
        />
      </SimpleTooltip>
    </div>
  );
};

interface OverheadFormModalProps {
  open: boolean;
  /** When set, the modal edits this overhead; otherwise it creates a new one. */
  overhead: OverheadListItem | null;
  onClose: () => void;
}

export const OverheadFormModal = ({ open, overhead, onClose }: OverheadFormModalProps) => {
  const { t } = useTranslation(['authenticated.overheads', 'common']);
  const isEditMode = Boolean(overhead);
  // Invoiced overheads are immutable (backend rejects updates with
  // OVERHEAD_ALREADY_INVOICED). The table already locks the actions, but the
  // entry can become invoiced after the list loaded — render read-only with an
  // explanatory warning instead of letting the save fail.
  const isInvoicedLocked = overhead?.status === OverheadStatus.Invoiced;
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const form = useForm<OverheadFormValues>({
    resolver: zodResolver(overheadFormSchema),
    defaultValues: getDefaultValues(overhead),
  });

  // Re-seed the form each time the modal opens (create defaults or the edited entity).
  useEffect(() => {
    if (open) form.reset(getDefaultValues(overhead));
  }, [open, overhead, form]);

  const watchedType = form.watch(FORM_FIELD_TYPE);
  const watchedFrequency = form.watch(FORM_FIELD_RECURRENCE_FREQUENCY);
  const isCostType = watchedType === OverheadType.Cost;
  const isRecurring = watchedFrequency !== OverheadRecurrenceFrequency.OneTime;

  // Mirror of the zod schema's blocking rules, computed from watched values so
  // the submit button can be disabled with an explanatory tooltip BEFORE the
  // user hits submit (schema validation only runs on submit). Same pattern as
  // the time-entry modal.
  const watchedName = form.watch(FORM_FIELD_NAME);
  const watchedProjectId = form.watch(FORM_FIELD_PROJECT_ID);
  const watchedAmount = form.watch(FORM_FIELD_AMOUNT);
  const watchedDate = form.watch(FORM_FIELD_DATE);
  const watchedRecurrenceEndDate = form.watch(FORM_FIELD_RECURRENCE_END_DATE);

  const amountNumber = Number(watchedAmount);
  const submitBlockReasons = [
    ...(!watchedName?.trim() ? [t('validation.nameRequired')] : []),
    ...(!isEditMode && !watchedProjectId ? [t('validation.projectRequired')] : []),
    ...(!watchedAmount
      ? [t('validation.amountRequired')]
      : isNaN(amountNumber) || amountNumber === 0
        ? [t('validation.amountNonZero')]
        : isCostType && amountNumber < 0
          ? [t('validation.amountPositive')]
          : []),
    ...(!isEditMode && isRecurring && !watchedRecurrenceEndDate
      ? [t('validation.recurrenceEndDateRequired')]
      : []),
    ...(!isEditMode &&
    isRecurring &&
    watchedRecurrenceEndDate &&
    watchedDate &&
    !isAfter(startOfDay(watchedRecurrenceEndDate), startOfDay(watchedDate))
      ? [t('validation.recurrenceEndDateAfterStart')]
      : []),
  ];
  const isSubmitBlocked = submitBlockReasons.length > 0;
  const submitBlockedTooltip =
    submitBlockReasons.length > 1 ? (
      <ul className="list-disc pl-4">
        {submitBlockReasons.map(reason => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    ) : submitBlockReasons.length === 1 ? (
      submitBlockReasons[0]
    ) : null;

  // Dividers appear only when the body actually scrolls (mirrors
  // add-onboarding-entities-modal). Field visibility changes affect the height.
  const { ref: scrollRef, isScrollable } = useScrollOverflow([
    open,
    isRecurring,
    isEditMode,
    isInvoicedLocked,
  ]);

  const createMutation = useCreateOverhead({ onSuccess: onClose });
  const updateMutation = useUpdateOverhead({ onSuccess: onClose });
  const statusMutation = useUpdateOverheadStatus({ onSuccess: onClose });
  const isSaving = createMutation.isPending || updateMutation.isPending || statusMutation.isPending;

  // Cost overheads can flip Planned ↔ Paid straight from the edit modal.
  const isStatusToggleVisible =
    isEditMode && !isInvoicedLocked && overhead?.type === OverheadType.Cost;
  const statusToggleTarget =
    overhead?.status === OverheadStatus.Planned ? OverheadStatus.Paid : OverheadStatus.Planned;

  const handleStatusToggle = () => {
    if (!overhead) return;
    statusMutation.mutate({ overheadId: overhead.id, status: statusToggleTarget });
  };

  const recurrenceOptions = useMemo(
    () => [
      { value: OverheadRecurrenceFrequency.OneTime, label: t('form.recurrence.oneTime') },
      { value: OverheadRecurrenceFrequency.Weekly, label: t('form.recurrence.weekly') },
      { value: OverheadRecurrenceFrequency.Monthly, label: t('form.recurrence.monthly') },
      { value: OverheadRecurrenceFrequency.Quarterly, label: t('form.recurrence.quarterly') },
      { value: OverheadRecurrenceFrequency.Yearly, label: t('form.recurrence.yearly') },
    ],
    [t]
  );

  const handleSubmit = form.handleSubmit(values => {
    if (isInvoicedLocked) return;

    const payload = {
      name: values.name.trim(),
      date: normalizeToStartOfDayUTC(values.date).toISOString(),
      amount: values.amount,
      notes: values.notes.trim() || null,
    };

    if (overhead) {
      updateMutation.mutate({ overheadId: overhead.id, data: payload });
      return;
    }

    const recurrenceFrequency = mapOverheadRecurrenceFrequencyToDTO(values.recurrenceFrequency);

    createMutation.mutate({
      ...payload,
      type: mapOverheadTypeToDTO(values.type),
      projectId: Number(values.projectId),
      recurrence:
        recurrenceFrequency && values.recurrenceEndDate
          ? {
              frequency: recurrenceFrequency,
              endDate: normalizeToStartOfDayUTC(values.recurrenceEndDate).toISOString(),
            }
          : null,
    });
  });

  const handleCloseRequest = () => {
    if (isSaving) return;
    if (form.formState.isDirty) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscard = () => {
    setIsDiscardConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={isOpen => !isOpen && handleCloseRequest()}>
        <DialogContent
          className="flex max-h-[85vh] max-w-xl flex-col gap-0"
          // Radix autofocuses (and selects) the first input on open, which also
          // baits password managers into treating Name as a login field.
          onOpenAutoFocus={event => event.preventDefault()}
        >
          <DialogHeader className={cn('shrink-0 pb-4', isScrollable && 'border-b')}>
            <DialogTitle>{isEditMode ? t('form.editTitle') : t('form.createTitle')}</DialogTitle>
            <DialogDescription>
              {isEditMode ? t('form.editSubtitle') : t('form.createSubtitle')}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-col overflow-hidden">
              <div ref={scrollRef} className="space-y-3 overflow-y-auto px-1 py-4">
                {isInvoicedLocked && (
                  <Alert variant="warning">
                    <AlertDescription className="flex items-center gap-1.5">
                      <TriangleAlertIcon size={16} className="shrink-0" />
                      {overhead?.invoice?.number
                        ? t('form.invoicedLockedAlert', { number: overhead.invoice.number })
                        : t('form.invoicedLockedAlertNoNumber')}
                    </AlertDescription>
                  </Alert>
                )}

                <FormField
                  control={form.control}
                  name={FORM_FIELD_NAME}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('form.nameLabel')}</FormLabel>
                      <FormControl>
                        {/* autoComplete off + data-1p-ignore keep password managers
                            (1Password) from offering autofill on this text field. */}
                        <Input
                          {...field}
                          placeholder={t('form.namePlaceholder')}
                          autoComplete="off"
                          data-1p-ignore
                          disabled={isInvoicedLocked}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <SingleSelectWithPagination
                  name={FORM_FIELD_PROJECT_ID}
                  control={form.control}
                  label={t('form.projectLabel')}
                  placeholder={t('form.projectPlaceholder')}
                  disabled={isEditMode}
                  isClearSelectionEnabled={false}
                  queryKey={PROJECT_SELECT_QUERY_KEY}
                  loadItems={cursor =>
                    getShortProjects({
                      pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
                    })
                  }
                  searchItems={(searchTerm, cursor) =>
                    getShortProjects({
                      filter: { searchString: searchTerm },
                      pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
                    })
                  }
                  renderOption={renderProjectOption}
                  renderTrigger={renderProjectTrigger}
                  initialSelectedItem={
                    overhead
                      ? {
                          id: String(overhead.project.id),
                          name: overhead.project.name,
                          colorHex: overhead.project.colorHex,
                          avatarLink: overhead.project.logoUrl ?? undefined,
                        }
                      : undefined
                  }
                />

                <FormField
                  control={form.control}
                  name={FORM_FIELD_TYPE}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('form.typeLabel')}</FormLabel>
                      <FormControl>
                        <div role="radiogroup" className="flex gap-2">
                          <TypeOption
                            value={OverheadType.Income}
                            label={t('form.typeIncome')}
                            tooltip={t('form.typeIncomeTooltip')}
                            isSelected={field.value === OverheadType.Income}
                            disabled={isEditMode}
                            onSelect={field.onChange}
                          />
                          <TypeOption
                            value={OverheadType.Cost}
                            label={t('form.typeCost')}
                            tooltip={t('form.typeCostTooltip')}
                            isSelected={field.value === OverheadType.Cost}
                            disabled={isEditMode}
                            onSelect={field.onChange}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 items-start gap-3">
                  <DatePicker
                    name={FORM_FIELD_DATE}
                    control={form.control}
                    label={t('form.dateLabel')}
                    captionLayout="dropdown"
                    inputDisabled={isInvoicedLocked}
                  />

                  <FormField
                    control={form.control}
                    name={FORM_FIELD_AMOUNT}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>{t('form.amountLabel')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DecimalInput
                              name={field.name}
                              value={field.value}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                              maxDecimalPlaces={FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES}
                              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
                              allowNegative={!isCostType}
                              placeholder={t('form.amountPlaceholder')}
                              className={overhead ? 'pr-12' : undefined}
                              disabled={isInvoicedLocked}
                            />
                            {overhead && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                {overhead.currencyCode}
                              </span>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Alert variant="info">
                  <AlertDescription className="flex items-center gap-1.5">
                    <InfoIcon size={16} className="shrink-0" />
                    {isCostType ? t('form.amountHintCost') : t('form.amountHintIncome')}
                  </AlertDescription>
                </Alert>

                {!isEditMode && (
                  <>
                    <div className="grid grid-cols-2 items-start gap-3">
                      <SingleSelect
                        name={FORM_FIELD_RECURRENCE_FREQUENCY}
                        control={form.control}
                        label={t('form.recurrence.label')}
                        options={recurrenceOptions}
                      />
                      {isRecurring && (
                        <DatePicker
                          name={FORM_FIELD_RECURRENCE_END_DATE}
                          control={form.control}
                          label={t('form.recurrence.endDateLabel')}
                          placeholder={t('form.recurrence.endDatePlaceholder')}
                          captionLayout="dropdown"
                        />
                      )}
                    </div>
                    {isRecurring && (
                      <Alert variant="info">
                        <AlertDescription className="flex items-center gap-1.5">
                          <InfoIcon size={16} className="shrink-0" />
                          {t('form.recurrence.currencyChangeHint')}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}

                {isEditMode && overhead?.seriesId !== null && overhead?.seriesId !== undefined && (
                  <Alert variant="info">
                    <AlertDescription className="flex items-center gap-1.5">
                      <InfoIcon size={16} className="shrink-0" />
                      {t('form.recurrence.occurrenceEditHint')}
                    </AlertDescription>
                  </Alert>
                )}

                <FormField
                  control={form.control}
                  name={FORM_FIELD_NOTES}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('form.notesLabel')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t('form.notesPlaceholder')}
                          className="resize-y"
                          rows={3}
                          disabled={isInvoicedLocked}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter
                className={cn(
                  'shrink-0 justify-between pt-4 sm:justify-between',
                  isScrollable && 'border-t'
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseRequest}
                  disabled={isSaving}
                >
                  {t('common:cancel')}
                </Button>
                {!isInvoicedLocked && (
                  <div className="flex items-center gap-2">
                    {isStatusToggleVisible && (
                      <ButtonLoadable
                        type="button"
                        variant="outline"
                        isLoading={statusMutation.isPending}
                        disabled={isSaving}
                        onClick={handleStatusToggle}
                      >
                        {statusToggleTarget === OverheadStatus.Paid
                          ? t('table.markPaid')
                          : t('table.markPlanned')}
                      </ButtonLoadable>
                    )}
                    <SimpleTooltip content={submitBlockedTooltip}>
                      <span>
                        <ButtonLoadable
                          type="submit"
                          isLoading={createMutation.isPending || updateMutation.isPending}
                          disabled={isSaving || isSubmitBlocked}
                          icon={isSubmitBlocked ? <InfoIcon className="h-4 w-4" /> : undefined}
                        >
                          {isEditMode ? t('form.saveChanges') : t('form.createSubmit')}
                        </ButtonLoadable>
                      </span>
                    </SimpleTooltip>
                  </div>
                )}
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation as a plain sibling Dialog (assignment-modal
          pattern). Radix stacks sibling dialogs correctly; the previous
          Modal-based confirmation rendered UNDER this dialog's overlay and
          froze the page behind an invisible confirm. */}
      <Dialog open={isDiscardConfirmOpen} onOpenChange={() => setIsDiscardConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('form.discardTitle')}</DialogTitle>
          </DialogHeader>
          <DialogDescription>{t('form.discardMessage')}</DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDiscardConfirmOpen(false)}>
              {t('form.discardCancel')}
            </Button>
            <Button variant="destructive" onClick={handleDiscard}>
              {t('form.discardConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
